import { useEffect, useState } from 'react';

import { useDuplicateCampaign } from '@givebacks/api/store';
import { useFeatureFlag_Core } from 'core';
import { EditAccountCodeModal, FacultyAccessColumn } from 'core/Components';
import { CellContext, useCrowdFundingTranslations } from 'core/Hooks';
import { AnchorCell, EmptyCell, Table } from 'core/Packages';
import { centsToDollar, getDate, getErrorMessage_v2 } from 'core/Utils';
import { Badge, BadgeProps, Divider, Text, notifications } from 'ui';
import {
  IconArrowLeft,
  IconClock,
  IconClose,
  IconCopy,
  IconEditTabler,
  IconLink,
  IconNotes,
  IconSparkles,
  IconStar,
  IconTicket,
} from 'ui/Icons';

import { CrowdfundingProvider, useCrowdfundingProvider } from '../../../../../Context';
import { CampaignStoreItem, useDeleteStoreItem, useUpdateStoreItem } from '../../../../../api';
import { useDeleteCampaign } from '../../../../../api/new/campaigns/delete';
import { useShowCampaignSettings } from '../../../../../api/new/campaigns/utils/campaignUtils';
import { useStoreAccountCodes } from '../../../../../hooks/useStoreAccountCodes';
import { useCampaignManagementNavigation } from '../../../../routes/management/route.context';
import { FinalizeCampaignModal } from '../Campaign/Modals/FinalizeCampaignModal';
import { AllCampaignsTableInstance } from './table';

const statusBadgeMap = new Map<string, BadgeProps>([
  [
    'draft',
    {
      children: 'Draft',
      variant: 'light',
      type: 'neutral',
      w: '100px',
    },
  ],
  [
    'scheduled',
    {
      children: 'Scheduled',
      variant: 'light',
      type: 'primary',
      leftSection: <IconClock size={12} />,
      w: '100px',
    },
  ],
  [
    'published',
    {
      children: 'Published',
      variant: 'light',
      type: 'primary',
      w: '100px',
    },
  ],
  [
    'finishing_required',
    {
      children: 'Finalization Required',
      variant: 'light',
      type: 'error',
      w: 'auto',
    },
  ],
  [
    'finished',
    {
      children: 'Finished',
      variant: 'filled',
      type: 'success',
      w: '100px',
    },
  ],
]);

const AccountCodeRowAction = ({
  setIsEditAccountCodeModalOpen,
  causeId,
  storeItemUuid,
}: {
  setIsEditAccountCodeModalOpen: (arg: boolean) => void;
  causeId: string;
  storeItemUuid: string;
}) => {
  const { accountCodeActionLabel } = useStoreAccountCodes({
    causeId: causeId,
    uuid: storeItemUuid,
  });
  const { isFeatureEnabled } = useFeatureFlag_Core({
    featureId: 'accountCodes',
  });

  return (
    <Table.RowAction.Option
      visible={isFeatureEnabled}
      label={accountCodeActionLabel}
      icon={IconEditTabler}
      onClick={() => setIsEditAccountCodeModalOpen(true)}
    />
  );
};

const CampaignActions = (props: CellContext<CampaignStoreItem, unknown>) => {
  const rowData = props.row.original;
  const campaignId = rowData.uuid;

  const [isFinalizeModalOpen, setIsFinalizeModalOpen] = useState(false);
  const [isEditAccountCodeModalOpen, setIsEditAccountCodeModalOpen] = useState(false);

  const { hasWriteAccess } = useStoreAccountCodes({
    causeId: props.row.original.cause_id,
    uuid: props.row.original.uuid,
  });
  const { setBackgroundLocation } = useCampaignManagementNavigation();
  const { mutate: duplicateCampaign } = useDuplicateCampaign();
  const { mutate: updateStoreItem } = useUpdateStoreItem();
  const { mutate: deleteCampaign } = useDeleteCampaign();
  const { causeId } = useCrowdfundingProvider();
  const { t: d } = useCrowdFundingTranslations(causeId);
  const {
    data: { isPeersEnabled, isCampaignDraft, isCampaignFinished, isCampaignDeleted },
  } = useShowCampaignSettings();

  const showArchiveCampaign = (isCampaignDraft || isCampaignFinished) && !isCampaignDeleted;
  const showCampaignDonationHistory = !isCampaignDraft;
  const showViewCampaignLinks = !isCampaignFinished;

  const showFinalizeCampaign =
    rowData.__type === 'athon' && rowData.status === 'finishing_required';

  return (
    <>
      <Table.RowAction>
        <Table.RowAction.Option
          label="View Campaign Links"
          icon={IconLink}
          onClick={() => setBackgroundLocation(`./${campaignId}/settings/links`)}
          visible={showViewCampaignLinks}
        />

        <Table.RowAction.Option
          label={`Campaign ${d('Donation')} History`}
          icon={IconNotes}
          onClick={() => setBackgroundLocation(`./${campaignId}`)}
          visible={showCampaignDonationHistory}
        />

        <Table.RowAction.Option
          label="Participants & Prize Reports"
          icon={IconStar}
          onClick={() => setBackgroundLocation(`./${campaignId}/reports/prizes`)}
          visible={isPeersEnabled}
        />

        <Table.RowAction.Option
          label="Duplicate Campaign"
          icon={IconCopy}
          onClick={() =>
            // TODO: invalidate all campaigns table
            duplicateCampaign(
              { campaignId, causeId: props.row.original.cause_id },
              {
                onSuccess: () => {
                  notifications.show({
                    variant: 'success',
                    message: 'Campaign duplicated successfully',
                  });
                },
                onError: () => {
                  notifications.show({
                    variant: 'error',
                    message: 'Campaign duplication failed',
                  });
                },
              }
            )
          }
        />

        <Table.RowAction.Option
          label="Edit Campaign"
          icon={IconEditTabler}
          onClick={() => setBackgroundLocation(`./${campaignId}/settings`)}
        />

        <AccountCodeRowAction
          setIsEditAccountCodeModalOpen={setIsEditAccountCodeModalOpen}
          causeId={props.row.original.cause_id}
          storeItemUuid={props.row.original.uuid}
        />

        <Table.RowAction.Option
          label="Finalize Campaign"
          icon={IconSparkles}
          onClick={() => {
            setIsFinalizeModalOpen(true);
          }}
          visible={showFinalizeCampaign}
        />

        <Table.RowAction.Option
          label="Archive Campaign"
          icon={IconClose}
          destructive
          onClick={() => {
            deleteCampaign(campaignId, {
              onSuccess: () => {
                notifications.show({
                  variant: 'success',
                  message: 'Campaign successfully archived',
                });
              },
              onError: () => {
                notifications.show({
                  variant: 'error',
                  message: 'Could not archive campaign',
                });
              },
            });
          }}
          visible={showArchiveCampaign}
        />

        <Table.RowAction.Option
          label="Unarchive Campaign"
          icon={IconArrowLeft}
          destructive
          onClick={() => {
            updateStoreItem(
              {
                causeId: props.row.original.cause_id,
                uuid: campaignId,
                store_item: {
                  deleted_at: null,
                },
              },
              {
                onSuccess: () => {
                  notifications.show({
                    variant: 'success',
                    message: 'Campaign successfully unarchived',
                  });
                },
                onError: () => {
                  notifications.show({
                    variant: 'error',
                    message: 'Could not unarchive campaign',
                  });
                },
              }
            );
          }}
          visible={isCampaignDeleted}
        />
      </Table.RowAction>

      <FinalizeCampaignModal
        opened={isFinalizeModalOpen}
        onClose={() => setIsFinalizeModalOpen(false)}
      />
      <EditAccountCodeModal
        opened={isEditAccountCodeModalOpen}
        onClose={() => setIsEditAccountCodeModalOpen(false)}
        accountCode={props.row.original.account_code}
        causeId={props.row.original.cause_id}
        storeItemId={props.row.original.uuid}
        feature="campaign"
        productItemProps={{
          name: props.row.original.name,
          campaignType: props.row.original.__type,
        }}
        hasWriteAccess={hasWriteAccess}
      />
    </>
  );
};

const RallyUpActions = (props: CellContext<CampaignStoreItem, unknown>) => {
  const { mutate: updateStoreItem } = useUpdateStoreItem();
  const { mutate: deleteCampaign } = useDeleteStoreItem();
  const [isEditAccountCodeModalOpen, setIsEditAccountCodeModalOpen] = useState(false);

  const { hasWriteAccess } = useStoreAccountCodes({
    causeId: props.row.original.cause_id,
    uuid: props.row.original.uuid,
  });

  const rallyUpLink = props.row.original.integration?.campaign_setup_url;
  const showArchive = !props.row.original.deleted_at;

  return (
    <>
      <Table.RowAction>
        <Table.RowAction.Option
          label="Manage Campaign"
          icon={IconLink}
          onClick={() => window.open(rallyUpLink)}
        />
        <AccountCodeRowAction
          setIsEditAccountCodeModalOpen={setIsEditAccountCodeModalOpen}
          causeId={props.row.original.cause_id}
          storeItemUuid={props.row.original.uuid}
        />
        <Table.RowAction.Option
          label="Archive Campaign"
          icon={IconClose}
          destructive
          onClick={() => {
            deleteCampaign(
              {
                causeId: props.row.original.cause_id,
                uuid: props.row.original.uuid,
              },
              {
                onSuccess: () => {
                  notifications.show({
                    variant: 'success',
                    message: 'Campaign successfully archived',
                  });
                },
                onError: () => {
                  notifications.show({
                    variant: 'error',
                    message: 'Could not archive campaign',
                  });
                },
              }
            );
          }}
          visible={showArchive}
        />

        <Table.RowAction.Option
          label="Unarchive Campaign"
          icon={IconArrowLeft}
          destructive
          onClick={() => {
            updateStoreItem(
              {
                causeId: props.row.original.cause_id,
                uuid: props.row.original.uuid,
                store_item: {
                  deleted_at: null,
                },
              },
              {
                onSuccess: () => {
                  notifications.show({
                    variant: 'success',
                    message: 'Campaign successfully unarchived',
                  });
                },
                onError: (e) => {
                  notifications.show({
                    variant: 'error',
                    message: getErrorMessage_v2(e, 'Could not unarchive campaign'),
                  });
                },
              }
            );
          }}
          visible={!showArchive}
        />
      </Table.RowAction>

      <EditAccountCodeModal
        opened={isEditAccountCodeModalOpen}
        onClose={() => setIsEditAccountCodeModalOpen(false)}
        accountCode={props.row.original.account_code}
        causeId={props.row.original.cause_id}
        storeItemId={props.row.original.uuid}
        feature="campaign"
        productItemProps={{
          name: props.row.original.name,
          campaignType: props.row.original.__type,
        }}
        hasWriteAccess={hasWriteAccess}
      />
    </>
  );
};

const TicketedEventActions = (props: CellContext<CampaignStoreItem, unknown>) => {
  const rowData = props.row.original;
  const campaignId = rowData.uuid;

  const { mutate: deleteCampaign } = useDeleteStoreItem();
  const { mutate: updateStoreItem } = useUpdateStoreItem();
  const { mutate: duplicateEvent } = useDuplicateCampaign();
  const { setBackgroundLocation } = useCampaignManagementNavigation();
  const [isEditAccountCodeModalOpen, setIsEditAccountCodeModalOpen] = useState(false);

  const { hasWriteAccess } = useStoreAccountCodes({
    causeId: props.row.original.cause_id,
    uuid: props.row.original.uuid,
  });

  const {
    data: { isCampaignDraft, isCampaignFinished, isCampaignDeleted },
  } = useShowCampaignSettings();

  const showArchiveCampaign = (isCampaignDraft || isCampaignFinished) && !isCampaignDeleted;

  return (
    <>
      <Table.RowAction>
        <Table.RowAction.Option
          label="Event Sales History"
          icon={IconNotes}
          onClick={() => setBackgroundLocation(`./${campaignId}`)}
          visible={!isCampaignDraft}
        />

        <Table.RowAction.Option
          label="Ticket Check-in"
          icon={IconTicket}
          onClick={() => setBackgroundLocation(`./${campaignId}/events/check-in`)}
          visible={!isCampaignDraft}
        />

        <Divider mx="sm" />

        <Table.RowAction.Option
          label="View Event Links"
          icon={IconLink}
          onClick={() => setBackgroundLocation(`./${campaignId}/settings/links`)}
        />

        <Divider mx="sm" />

        <Table.RowAction.Option
          label="Duplicate Event"
          icon={IconCopy}
          onClick={() =>
            // TODO: invalidate all campaigns table
            duplicateEvent(
              { campaignId: campaignId, causeId: props.row.original.cause_id },
              {
                onSuccess: () => {
                  notifications.show({
                    variant: 'success',
                    message: 'Event duplicated successfully',
                  });
                },
                onError: () => {
                  notifications.show({
                    variant: 'error',
                    message: 'Event duplication failed',
                  });
                },
              }
            )
          }
        />

        <Table.RowAction.Option
          label="Edit Event"
          icon={IconEditTabler}
          onClick={() => setBackgroundLocation(`./${campaignId}/settings`)}
        />

        <AccountCodeRowAction
          setIsEditAccountCodeModalOpen={setIsEditAccountCodeModalOpen}
          causeId={props.row.original.cause_id}
          storeItemUuid={props.row.original.uuid}
        />

        <Table.RowAction.Option
          label="Archive Event"
          icon={IconClose}
          destructive
          onClick={() => {
            deleteCampaign(
              { causeId: props.row.original.cause_id, uuid: campaignId },
              {
                onSuccess: () => {
                  notifications.show({
                    variant: 'success',
                    message: 'Event successfully archived',
                  });
                },
                onError: () => {
                  notifications.show({
                    variant: 'error',
                    message: 'Could not archive event',
                  });
                },
              }
            );
          }}
          visible={showArchiveCampaign}
        />

        <Table.RowAction.Option
          label="Unarchive Event"
          icon={IconArrowLeft}
          destructive
          onClick={() => {
            updateStoreItem(
              {
                causeId: props.row.original.cause_id,
                uuid: campaignId,
                store_item: {
                  deleted_at: null,
                },
              },
              {
                onSuccess: () => {
                  notifications.show({
                    variant: 'success',
                    message: 'Event successfully unarchived',
                  });
                },
                onError: () => {
                  notifications.show({
                    variant: 'error',
                    message: 'Could not unarchive event',
                  });
                },
              }
            );
          }}
          visible={isCampaignDeleted}
        />
      </Table.RowAction>

      <EditAccountCodeModal
        opened={isEditAccountCodeModalOpen}
        onClose={() => setIsEditAccountCodeModalOpen(false)}
        accountCode={props.row.original.account_code}
        causeId={props.row.original.cause_id}
        storeItemId={props.row.original.uuid}
        feature="campaign"
        productItemProps={{
          name: props.row.original.name,
          campaignType: props.row.original.__type,
        }}
        hasWriteAccess={hasWriteAccess}
      />
    </>
  );
};

const CrowdFundingActions = (props: CellContext<CampaignStoreItem, unknown>) => {
  const { setCrowdfundingCampaign, campaignId } = useCrowdfundingProvider();

  useEffect(() => {
    if (campaignId !== props.row.original.uuid) {
      setCrowdfundingCampaign(props.row.original.uuid);
    }
  }, [props.row.original.uuid]);

  switch (props.row.original.type) {
    case 'StoreItems::RallyUp':
      return <RallyUpActions {...props} />;

    case 'StoreItems::Event':
      return <TicketedEventActions {...props} />;

    default:
      return <CampaignActions {...props} />;
  }
};

const CampaignName = (props: CellContext<CampaignStoreItem, unknown>) => {
  const isRallyUp = props.row.original.type === 'StoreItems::RallyUp';
  const { setBackgroundLocation } = useCampaignManagementNavigation();

  return (
    <AnchorCell
      component="button"
      data-campaignLink={!isRallyUp ? true : undefined}
      onClick={() => !isRallyUp && setBackgroundLocation(`./${props.row.original.uuid}`)}
    >
      {props.row.original.name}
    </AnchorCell>
  );
};

const AmountRaised = (props: CellContext<CampaignStoreItem, unknown>) => {
  const isRallyUp = props.row.original.type === 'StoreItems::RallyUp';
  const isFundraiserFinished = props.row.original.status === 'finished';

  const amountRaised =
    isRallyUp && !isFundraiserFinished ? '*' : centsToDollar(props.row.original.amount_raised);

  return (
    <Text type="caption" c="gray">
      {amountRaised}
    </Text>
  );
};

export const AllCampaignsColumns = [
  AllCampaignsTableInstance.columnHelper.accessor('name', {
    header: 'Name',
    id: 'name',
    cell: (props) => <CampaignName {...props} />,
    size: 200,
  }),
  AllCampaignsTableInstance.columnHelper.accessor('type', {
    header: 'Type',
    id: 'type',
    cell: (props) => {
      switch (props.row.original.type) {
        case 'StoreItems::Athon':
          return 'A-thon';

        case 'StoreItems::RallyUp':
          return 'Fundraiser';

        case 'StoreItems::Event':
          return 'Ticketed Event';

        default:
          return 'Crowdfunding';
      }
    },
    size: 200,
    enableSorting: true,
  }),

  AllCampaignsTableInstance.columnHelper.accessor('status', {
    header: 'Status',
    id: 'status',
    enableSorting: true,
    cell: (props) => {
      return <Badge {...statusBadgeMap.get(props.row.original.status)} />;
    },
    size: 200,
  }),
  AllCampaignsTableInstance.columnHelper.accessor('account_code', {
    header: 'Account Code',
    id: 'account_code',
    cell: (props) => props.row.original.account_code || <EmptyCell />,
    size: 200,
  }),
  AllCampaignsTableInstance.columnHelper.accessor('starting_at', {
    header: 'Start Date',
    id: 'starting_at',
    cell: (props) =>
      props.row.original.starting_at ? getDate(props.row.original.starting_at) : <EmptyCell />,
    size: 50,
  }),
  AllCampaignsTableInstance.columnHelper.accessor('expires_at', {
    header: 'End Date',
    id: 'expires_at',
    cell: (props) =>
      props.row.original.expires_at ? getDate(props.row.original.expires_at) : <EmptyCell />,
    size: 50,
  }),
  // TODO: @bkchu - `goal` and `amount_raised` are returning 0 from the backend for null
  // values. Issue: https://github.com/memberhubteam/store-api/issues/203
  AllCampaignsTableInstance.columnHelper.accessor('goal', {
    header: 'Goal',
    id: 'goal',
    cell: (props) => centsToDollar(props.getValue()),
    size: 75,
    enableSorting: false,
  }),
  AllCampaignsTableInstance.columnHelper.accessor('amount_raised', {
    header: 'Amount Raised',
    id: 'amount_raised',
    cell: (props) => <AmountRaised {...props} />,
    size: 75,
    enableSorting: false,
  }),
  AllCampaignsTableInstance.columnHelper.accessor('user', {
    header: 'Creator',
    cell: (props) => <FacultyAccessColumn user={props.row.original.user} />,
    enableSorting: false,
  }),
  AllCampaignsTableInstance.columnHelper.display({
    id: 'actions',
    cell: (props) => (
      <CrowdfundingProvider>
        <CrowdFundingActions {...props} />
      </CrowdfundingProvider>
    ),
    size: 40,
  }),
];
